/* eslint-disable no-param-reassign */
import React, { lazy, useState, useEffect } from 'react';
import CustomTextField from 'components/Forms/TextField';
import CustomRadio from 'components/Forms/Radio';
import CustomSelect from 'components/Forms/CustomSelect';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Form, Field, FormSpy } from 'react-final-form';
import { Box, Grid, Button } from '@material-ui/core';
import { useAsyncFn } from 'react-use';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  transformDataToSelectOptions,
  generateErrorObject,
  transformDataCountriesToSelectOptions,
  isEditSaveDisabled,
} from 'utils';
import { getTrainingModulesList } from 'http/trainingModules';
import { getCountries } from 'http/info';
import configErrors from './configErrors';
import validate from './validation';
import useStyles from './styles';

const toModuleOption = item => {
  if (item == null) {
    return null;
  }
  if (item.value != null) {
    return { value: item.value, label: item.label };
  }
  if (item.id == null) {
    return null;
  }
  return { value: item.id, label: item.name };
};

const mergeModuleOptions = (existing, assigned) => {
  const byId = new Map();
  [...(existing || []), ...(assigned || [])].forEach(item => {
    const option = toModuleOption(item);
    if (option && option.value != null && !byId.has(option.value)) {
      byId.set(option.value, option);
    }
  });
  return Array.from(byId.values());
};

const Loader = lazy(() => import('components/Loader'));

const TrainersForm = ({ onSubmit, isLoading, trainersData, trainerType, backUrl, setChosenTrainingModules, type }) => {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation('common');

  const { station } = useSelector(state => state.auth.profile);
  const stationId = station ? station.id : undefined;

  const [formData, setFormData] = useState(trainersData || {});
  const [currentStationId, setCurrentStationId] = useState(stationId);
  const [formControlsOptions, setFormControlsOptions] = useState({
    trainingModules: [],
    countries: [],
  });

  const [formControlsState, getFormControlsOptions] = useAsyncFn(async () => {
    const modules = await getTrainingModulesList({ status: 'active' });
    const countries = await getCountries();

    const optionsObj = {
      trainingModules: [],
      countries: [],
    };

    if (modules.items) {
      optionsObj.trainingModules = transformDataToSelectOptions(modules.items, 'id', 'name');
    }

    if (countries && !countries.error) {
      optionsObj.countries = transformDataCountriesToSelectOptions(countries);
    }

    setFormControlsOptions(prevState => ({
      ...prevState,
      ...optionsObj,
      trainingModules: mergeModuleOptions(optionsObj.trainingModules, prevState.trainingModules),
    }));
  });

  useEffect(() => {
    if (trainersData) {
      const selectedModules = trainersData.trainingModules || [];
      setFormData({
        ...trainersData,
        trainingModules: transformDataToSelectOptions(selectedModules, 'id', 'name'),
        country: transformDataCountriesToSelectOptions(trainersData.country),
      });
      setChosenTrainingModules(selectedModules);
      setFormControlsOptions(prevState => ({
        ...prevState,
        trainingModules: mergeModuleOptions(prevState.trainingModules, selectedModules),
      }));
    }
    return () => {
      trainersData = {};
    };
  }, [trainersData, setChosenTrainingModules]);

  useEffect(() => {
    getFormControlsOptions();
  }, []);

  useEffect(() => {
    setCurrentStationId(station.id);
  }, [station]);

  const { isExternal = 'false', isComplaint = 'true' } = formData;

  const handleChangeRadio = (name, val) => {
    setFormData({ ...formData, [name]: val });
  };

  const handleFormSubmit = async formValues => {
    const transformedFormData = {
      ...formValues,
      trainingModuleIds: (formValues.trainingModules || []).map(item => item.value),
      country: formValues.country.value,
      isComplaint: JSON.parse(isComplaint),
      isExternal: JSON.parse(isExternal),
    };

    delete transformedFormData.trainingModules;

    const submitData = await onSubmit(transformedFormData, currentStationId);
    if (submitData && submitData.error && submitData.message) {
      return generateErrorObject(submitData.message, configErrors);
    }

    setChosenTrainingModules(
      (formValues.trainingModules || []).map(item => ({
        id: item.value,
        name: item.label,
      })),
    );

    return undefined;
  };

  return (
    <Form
      onSubmit={handleFormSubmit}
      validate={validate}
      initialValues={formData}
      keepDirtyOnReinitialize
      render={({ handleSubmit, submitting, pristine }) => (
        <form onSubmit={handleSubmit}>
          <FormSpy
            onChange={({ modified, values: formValues }) => {
              if (modified.isExternal) {
                handleChangeRadio('isExternal', formValues.isExternal);
              }

              if (modified.isComplaint) {
                handleChangeRadio('isComplaint', formValues.isComplaint);
              }
            }}
          />
          <Box className={classes.formWrap} display="flex">
            <Box className={classes.generalInfo}>
              <Box width="100%" mb={4}>
                <Field
                  type="radio"
                  id="isExternal"
                  name="isExternal"
                  label={t('formLabels.type')}
                  disabled={submitting}
                  fullWidth
                  component={CustomRadio}
                  items={[
                    { name: t('trainers.trainer'), value: 'false' },
                    { name: t('trainers.externalProvider'), value: 'true' },
                  ]}
                  value={isExternal}
                  defaultValue={isExternal}
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="text"
                  id="firstName"
                  name="firstName"
                  label={t('formLabels.firstName')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="text"
                  id="lastName"
                  name="lastName"
                  label={t('formLabels.lastName')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="email"
                  id="email"
                  name="email"
                  label={t('formLabels.email')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>

              <Box width="100%" mb={0}>
                <Field
                  type="text"
                  id="ids"
                  name="ids"
                  label={t('formLabels.idNumber')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>
            </Box>

            <Box className={classes.additionalInfo}>
              <Box width="100%" mb={4}>
                <Field
                  type="radio"
                  id="isComplaint"
                  name="isComplaint"
                  label={t('formLabels.state')}
                  disabled={submitting}
                  fullWidth
                  component={CustomRadio}
                  items={[
                    { name: t('trainers.compliant'), value: 'true' },
                    { name: t('trainers.notCompliant'), value: 'false' },
                  ]}
                  value={isComplaint}
                  defaultValue={isComplaint}
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="text"
                  id="company"
                  name="company"
                  label={t('formLabels.company')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="text"
                  id="country"
                  name="country"
                  label={t('formLabels.country')}
                  variant="outlined"
                  placeholder={t('placeholders.country')}
                  options={formControlsOptions.countries}
                  fullWidth
                  isMulti={false}
                  component={CustomSelect}
                />
              </Box>

              <Box width="100%" mb={4}>
                <Field
                  type="text"
                  id="phone"
                  name="phone"
                  label={t('formLabels.phone')}
                  disabled={submitting}
                  variant="outlined"
                  fullWidth
                  component={CustomTextField}
                  format={(value = '') => value.trim()}
                  formatOnBlur
                />
              </Box>

              <Box width="100%" mb={0}>
                <Field
                  type="text"
                  id="trainingModules"
                  name="trainingModules"
                  label={t('formLabels.trainingModule')}
                  variant="outlined"
                  placeholder={t('placeholders.chooseTrainingModule')}
                  options={formControlsOptions.trainingModules}
                  fullWidth
                  isMulti
                  component={CustomSelect}
                />
              </Box>
            </Box>
          </Box>

          <Grid container spacing={3} className={classes.btnContainer}>
            <Grid item xs={6}>
              <Box textAlign="right">
                <Button
                  className={classes.btn}
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isEditSaveDisabled({ submitting, pristine, isEditMode: type === 'edit' })}
                  size="large"
                >
                  {t('buttons.save')}
                </Button>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box textAlign="left">
                <Button
                  className={classes.btn}
                  variant="outlined"
                  size="large"
                  type="reset"
                  onClick={() => {
                    history.push(backUrl, {
                      trainerType,
                    });
                  }}
                >
                  {t('buttons.cancel')}
                </Button>
              </Box>
            </Grid>
          </Grid>

          {(isLoading || formControlsState.loading) && <Loader />}
        </form>
      )}
    />
  );
};

TrainersForm.defaultProps = {
  trainersData: {
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    country: '',
    phone: '',
    isComplaint: 'true',
    isExternal: 'false',
  },
  setChosenTrainingModules: () => {},
  type: 'create',
};

TrainersForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
  trainersData: PropTypes.oneOfType([
    PropTypes.shape({
      firstName: PropTypes.string,
      lastName: PropTypes.string,
      email: PropTypes.string,
      company: PropTypes.string,
      country: PropTypes.string,
      phone: PropTypes.string,
      isComplaint: PropTypes.bool,
      isExternal: PropTypes.bool,
    }),
    PropTypes.bool,
  ]),
  backUrl: PropTypes.string.isRequired,
  trainerType: PropTypes.string.isRequired,
  setChosenTrainingModules: PropTypes.func,
  type: PropTypes.string,
};

export default TrainersForm;
